import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Divider,
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const WHEAT_VARIETIES = [
  { value: 'hrw', label: 'HRW - Hard Red Winter' },
  { value: 'hrs', label: 'HRS - Hard Red Spring' },
  { value: 'srw', label: 'SRW - Soft Red Winter' },
  { value: 'hw', label: 'HW - Hard White' },
  { value: 'sw', label: 'SW - Soft White' },
  { value: 'dur', label: 'DUR - Durum' },
];

const MAIZE_VARIETIES = [
  { value: 'wm', label: 'WM - White Maize' },
  { value: 'ym', label: 'YM - Yellow Maize' },
  { value: 'wx', label: 'WX - Waxy Maize' },
];

const COUNTRIES = ['USA', 'Canada', 'Australia', 'Russia', 'Ukraine', 'Argentina', 'Brazil', 'France', 'Germany', 'South Africa', 'Zambia', 'Zimbabwe', 'Kenya', 'Tanzania'];

const GrainLotForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id && !window.location.pathname.endsWith('/new');

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    supplierName: '',
    supplierRef: '',
    countryOfOrigin: '',
    grainType: 'wheat',
    grainVariety: '',
    cropYear: new Date().getFullYear(),
    receivedDate: new Date().toISOString().split('T')[0],
    receivedQtyMt: '',
    moisturePercent: '',
    proteinPercent: '',
    testWeightKgHl: '',
    fallingNumber: '',
    wetGlutenPercent: '',
    ashPercent: '',
    foreignMatterPercent: '',
    damagedKernelsPercent: '',
    aflatoxinPpb: '',
    unitCost: '',
    currency: 'USD',
    notes: '',
  });

  useEffect(() => {
    if (isEdit && id) {
      millingApi.getGrainLot(id).then((lot: any) => {
        setForm({
          supplierName: lot.supplierName || '',
          supplierRef: lot.supplierRef || '',
          countryOfOrigin: lot.countryOfOrigin || '',
          grainType: lot.grainType || 'wheat',
          grainVariety: lot.grainVariety || '',
          cropYear: lot.cropYear || new Date().getFullYear(),
          receivedDate: lot.receivedDate || new Date().toISOString().split('T')[0],
          receivedQtyMt: lot.receivedQtyMt != null ? String(lot.receivedQtyMt) : '',
          moisturePercent: lot.moisturePercent != null ? String(lot.moisturePercent) : '',
          proteinPercent: lot.proteinPercent != null ? String(lot.proteinPercent) : '',
          testWeightKgHl: lot.testWeightKgHl != null ? String(lot.testWeightKgHl) : '',
          fallingNumber: lot.fallingNumber != null ? String(lot.fallingNumber) : '',
          wetGlutenPercent: lot.wetGlutenPercent != null ? String(lot.wetGlutenPercent) : '',
          ashPercent: lot.ashPercent != null ? String(lot.ashPercent) : '',
          foreignMatterPercent: lot.foreignMatterPercent != null ? String(lot.foreignMatterPercent) : '',
          damagedKernelsPercent: lot.damagedKernelsPercent != null ? String(lot.damagedKernelsPercent) : '',
          aflatoxinPpb: lot.aflatoxinPpb != null ? String(lot.aflatoxinPpb) : '',
          unitCost: lot.unitCost != null ? String(lot.unitCost) : '',
          currency: lot.currency || 'USD',
          notes: lot.notes || '',
        });
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) =>
    setForm((f) => ({ ...f, [field]: (e.target as any).value }));

  const numOrUndef = (v: string) => v !== '' ? Number(v) : undefined;

  const handleSave = async () => {
    if (!form.supplierName || !form.receivedQtyMt || !form.receivedDate) {
      setError('Supplier name, received date, and quantity are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        supplierName: form.supplierName,
        supplierRef: form.supplierRef || undefined,
        countryOfOrigin: form.countryOfOrigin || undefined,
        grainType: form.grainType,
        grainVariety: form.grainVariety || undefined,
        cropYear: form.cropYear ? Number(form.cropYear) : undefined,
        receivedDate: form.receivedDate,
        receivedQtyMt: Number(form.receivedQtyMt),
        moisturePercent: numOrUndef(form.moisturePercent),
        proteinPercent: numOrUndef(form.proteinPercent),
        testWeightKgHl: numOrUndef(form.testWeightKgHl),
        fallingNumber: form.fallingNumber !== '' ? Number(form.fallingNumber) : undefined,
        wetGlutenPercent: numOrUndef(form.wetGlutenPercent),
        ashPercent: numOrUndef(form.ashPercent),
        foreignMatterPercent: numOrUndef(form.foreignMatterPercent),
        damagedKernelsPercent: numOrUndef(form.damagedKernelsPercent),
        aflatoxinPpb: numOrUndef(form.aflatoxinPpb),
        unitCost: numOrUndef(form.unitCost),
        currency: form.currency,
        notes: form.notes || undefined,
      };

      if (isEdit && id) {
        await millingApi.updateGrainLot(id, payload);
      } else {
        await millingApi.createGrainLot(payload);
      }
      navigate('/milling/grain-lots');
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const varieties = form.grainType === 'wheat' ? WHEAT_VARIETIES : MAIZE_VARIETIES;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/milling/grain-lots')}>Back</Button>
        <Typography variant="h4">{isEdit ? 'Edit Grain Lot' : 'Receive Grain'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Supplier & Origin</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Supplier Name *" value={form.supplierName} onChange={set('supplierName')} fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Supplier Reference" value={form.supplierRef} onChange={set('supplierRef')} fullWidth />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Country of Origin</InputLabel>
                    <Select value={form.countryOfOrigin} label="Country of Origin" onChange={(e) => setForm(f => ({ ...f, countryOfOrigin: e.target.value }))}>
                      <MenuItem value="">—</MenuItem>
                      {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Grain Type *</InputLabel>
                    <Select value={form.grainType} label="Grain Type *" onChange={(e) => setForm(f => ({ ...f, grainType: e.target.value, grainVariety: '' }))}>
                      <MenuItem value="wheat">Wheat</MenuItem>
                      <MenuItem value="maize">Maize</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Variety</InputLabel>
                    <Select value={form.grainVariety} label="Variety" onChange={(e) => setForm(f => ({ ...f, grainVariety: e.target.value }))}>
                      <MenuItem value="">—</MenuItem>
                      {varieties.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Crop Year" type="number" value={form.cropYear} onChange={set('cropYear')} fullWidth inputProps={{ min: 2000, max: 2100 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Received Date *" type="date" value={form.receivedDate} onChange={set('receivedDate')} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Received Qty (MT) *" type="number" value={form.receivedQtyMt} onChange={set('receivedQtyMt')} fullWidth inputProps={{ min: 0, step: 0.001 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Unit Cost" type="number" value={form.unitCost} onChange={set('unitCost')} fullWidth inputProps={{ min: 0, step: 0.0001 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select value={form.currency} label="Currency" onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="GBP">GBP</MenuItem>
                      <MenuItem value="KES">KES</MenuItem>
                      <MenuItem value="ZMW">ZMW</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quality Parameters */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quality Parameters</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField label="Moisture %" type="number" value={form.moisturePercent} onChange={set('moisturePercent')} fullWidth inputProps={{ min: 0, max: 100, step: 0.01 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Protein %" type="number" value={form.proteinPercent} onChange={set('proteinPercent')} fullWidth inputProps={{ min: 0, max: 100, step: 0.01 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Test Weight (kg/hl)" type="number" value={form.testWeightKgHl} onChange={set('testWeightKgHl')} fullWidth inputProps={{ min: 0, step: 0.1 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Falling Number" type="number" value={form.fallingNumber} onChange={set('fallingNumber')} fullWidth inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Wet Gluten %" type="number" value={form.wetGlutenPercent} onChange={set('wetGlutenPercent')} fullWidth inputProps={{ min: 0, max: 100, step: 0.01 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Ash %" type="number" value={form.ashPercent} onChange={set('ashPercent')} fullWidth inputProps={{ min: 0, max: 10, step: 0.001 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Foreign Matter %" type="number" value={form.foreignMatterPercent} onChange={set('foreignMatterPercent')} fullWidth inputProps={{ min: 0, max: 100, step: 0.001 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label="Damaged Kernels %" type="number" value={form.damagedKernelsPercent} onChange={set('damagedKernelsPercent')} fullWidth inputProps={{ min: 0, max: 100, step: 0.001 }} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label="Aflatoxin (ppb)"
                    type="number"
                    value={form.aflatoxinPpb}
                    onChange={set('aflatoxinPpb')}
                    fullWidth
                    inputProps={{ min: 0, step: 0.001 }}
                    helperText="Max 10 ppb for approval"
                    error={form.aflatoxinPpb !== '' && Number(form.aflatoxinPpb) > 10}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <TextField label="Notes" value={form.notes} onChange={set('notes')} fullWidth multiline rows={3} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/milling/grain-lots')}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              {isEdit ? 'Save Changes' : 'Receive Grain Lot'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrainLotForm;
